import React, { useCallback, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Box, Button, Checkbox, Chip, Divider, Drawer, Fab, IconButton, Paper, Popover, Stack, Typography } from "@mui/material";
import { useEffect } from "react";
import CreateApi from '../Components/Settings/appSettings';
import { formatDateForDisplay, utcToLocalDateString } from "@moment-golf/shared";
import { EventCardSection } from "./EventCardSection";
import { useAuth } from 'react-oidc-context';
import { useMCContext } from './contexts/useMCStates';
import { getCookie, deleteCookie } from './contexts/CookieContext';
import { MCHeader } from './Components/MCHeader';
import { PriceDisplayModeBar } from './Components/PriceDisplayModeBar';
import PullToRefresh from './services/PullToRefresh';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuSpanHeader from './Components/MenuSpanHeader';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import CloseIcon from '@mui/icons-material/Close';
import { MCHomePage } from './MCHomePage';
import { LeaderboardView } from './LeaderboardView';
import { MCStandingsSection } from './MCStandingsSection';

const QUILTED_FLAG_LOGO = `${process.env.PUBLIC_URL || ''}/Quilted Flag - Bold Zoom Tilt.png`;
const SCRIPT_CURSIVE_LOGO = `${process.env.PUBLIC_URL || ''}/Stacked Cursive.png`;


export const MomentCentral: React.FC<any> = () => {

    const api = CreateApi();
    const auth = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const { eventData, setCartItems, userRegistrations, setUserRegistrations, factor, windowSize, appTheme, userData, setUserData } = useMCContext();

    const stackRef = useRef<HTMLDivElement>(null);

    const [tabValue, setTabValue] = useState<string>('home');

    useEffect(() => {
        if (location.state?.tab === 'search') {
            setTabValue('search');
        } else if (location.state?.tab === 'trips') {
            setTabValue('trips');
        }
    }, [location.pathname, location.state?.tab]);
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [leaderboardEvent, setLeaderboardEvent] = useState<any | null>(null);

    // My Events state
    const [userRegisteredEvents, setUserRegisteredEvents] = useState<any>({ events: [], registrations: [] });
    const [editEvent, setEditEvent] = useState<any>(null);
    const [imageCache, setImageCache] = useState<any>({});
    const [transitioning, setTransitioning] = useState<boolean>(false);
    const [missingEvents, setMissingEvents] = useState<any>([]);
    const [openMissingEvents, setOpenMissingEvents] = useState<boolean>(false);
    const [myEventsPopperAnchorEl, setMyEventsPopperAnchorEl] = useState<HTMLElement | null>(null);
    const [myEventsPopperEventId, setMyEventsPopperEventId] = useState<string | null>(null);
    const [leaderboardDrawerEvent, setLeaderboardDrawerEvent] = useState<any | null>(null);
    const [standingsEvent, setStandingsEvent] = useState<any | null>(null);

    // My Trips state
    const [userTrips, setUserTrips] = useState<any[]>([]);
    const [selectedTrip, setSelectedTrip] = useState<any | null>(null);

    const isAuthenticated = auth.isAuthenticated;

    // On load effects
    useEffect(() => {
        const preventBounce = (e: TouchEvent) => {
            if (e.target instanceof Element) {
                const element = e.target.closest('.scrollable-content');
                if (element) {
                    const scrollTop = element.scrollTop;
                    const scrollHeight = element.scrollHeight;
                    const height = element.clientHeight;
                    if (scrollTop === 0 && e.touches[0].screenY > e.touches[0].pageY) {
                        e.preventDefault();
                    } else if (scrollHeight - scrollTop === height && e.touches[0].screenY < e.touches[0].pageY) {
                        e.preventDefault();
                    }
                }
            }
        };
        document.addEventListener('touchmove', preventBounce, { passive: false });
        return () => document.removeEventListener('touchmove', preventBounce);
    }, []);

    useEffect(() => {
        // If the registration success page flagged that the cart was just paid and cleared,
        // skip restoring from cookie. Only consume the flag (and delete the cookie) once
        // auth has loaded and we have the sub — this effect fires twice on a fresh page
        // load (sub=undefined first, then the real sub), so we must not remove the flag
        // on the first fire or the second fire will restore the cookie.
        if (sessionStorage.getItem('cart-cleared')) {
            if (auth?.user?.profile?.sub) {
                sessionStorage.removeItem('cart-cleared');
                deleteCookie(`${auth.user.profile.sub}-cartItems`);
            }
            setCartItems([]);
        } else {
            const cookieValue = getCookie(`${auth?.user?.profile?.sub}-cartItems`);
            setCartItems(cookieValue ? JSON.parse(cookieValue) : []);
        }

        api.get(`/mc/UserRegistrations?userID=${auth?.user?.profile?.sub}`)
            .then(response => { setUserRegistrations(response.data); })
            .catch(error => { console.error('Error fetching user registrations:', error); });
    }, [auth?.user?.profile?.sub]);

    const getRegistrations = async () => {
        const events = await api.get(`/myaccount/GetUserRegistrations?userID=${auth.user?.profile.sub}`);
        setUserRegisteredEvents({
            events: events.data.events,
            registrations: events.data.registrations,
        });
    };

    useEffect(() => {
        if (auth.isAuthenticated && auth.user?.profile?.sub) {
            const checkUser = async () => {
                try {
                    const user = await api.get(`/myaccount/GetOwnProfile`);
                    if (user.data) {
                        setUserData(user.data);
                        await getRegistrations();
                        const tripsRes = await api.get(`/mc/GetUserTrips?userID=${auth.user?.profile?.sub}`);
                        setUserTrips(tripsRes.data || []);
                    }
                } catch (error) {
                    if (!axios.isAxiosError(error) || error.response?.status !== 404) {
                        console.error('Error loading user:', error);
                    }
                }
            };
            void checkUser();
        }
    }, [auth.isAuthenticated, auth.user?.profile?.sub]);

    useEffect(() => {
        userRegisteredEvents.events.forEach((event: any) => {
            if (event.eventCardImageURL && !imageCache[event.eventCardImageURL]) {
                fetch(event.eventCardImageURL)
                    .then(response => response.blob())
                    .then(blob => {
                        if (!imageCache[event.eventCardImageURL]) {
                            const objectURL = URL.createObjectURL(blob);
                            setImageCache((prev: any) => ({ ...prev, [event.eventCardImageURL]: objectURL }));
                        }
                    })
                    .catch(error => console.error('Error caching image:', error));
            }
        });
    }, [userRegisteredEvents, imageCache]);

    useEffect(() => {
        userTrips.forEach((trip: any) => {
            if (trip.groupCardImageURL && !imageCache[trip.groupCardImageURL]) {
                fetch(trip.groupCardImageURL)
                    .then(response => response.blob())
                    .then(blob => {
                        if (!imageCache[trip.groupCardImageURL]) {
                            const objectURL = URL.createObjectURL(blob);
                            setImageCache((prev: any) => ({ ...prev, [trip.groupCardImageURL]: objectURL }));
                        }
                    })
                    .catch(error => console.error('Error caching trip image:', error));
            }
        });
    }, [userTrips, imageCache]);

    const handleNavigateToLiveScoring = useCallback(() => {
        window.open(process.env.REACT_APP_LIVESCORING_URL, '_blank');
    }, []);

    const handleLiveScoringClick = useCallback(() => {
        handleNavigateToLiveScoring();
        setDrawerOpen(false);
    }, [handleNavigateToLiveScoring]);

    const isMobileView = windowSize.isMobile || windowSize.verticalPC !== 'none' || ['small'].includes(windowSize.horizontalPC);
    const handleEventClick = useCallback((item: any) => {
        setEditEvent(item);
        setStandingsEvent(null);
        if (isMobileView) {
            setLeaderboardDrawerEvent(null);
            setMyEventsPopperEventId(null);
            setMyEventsPopperAnchorEl(null);
        }
    }, [isMobileView]);
    const handleClose = useCallback(() => { setEditEvent(null); setStandingsEvent(null); }, []);
    const handleCloseMobileDrawer = useCallback(() => {
        setEditEvent(null);
        setLeaderboardDrawerEvent(null);
        setMyEventsPopperEventId(null);
        setMyEventsPopperAnchorEl(null);
    }, []);

    const menuItems = useMemo(() => {
        const base = [
            { matchName: 'home',        label: 'Home',        requiresAuth: false },
            { matchName: 'search',      label: 'Search',      requiresAuth: false },
            { matchName: 'leaderboards', label: 'Leaderboards', requiresAuth: false },
            { matchName: 'standings',   label: 'Standings',   requiresAuth: false },
            { matchName: 'events',      label: 'My Events',   requiresAuth: true  },
            { matchName: 'trips',       label: 'My Trips',    requiresAuth: true  },
            { matchName: 'teams',       label: 'My Teams',    requiresAuth: true  },
        ];
        const liveScoring = windowSize.isMobile
            ? [{ matchName: 'live-scoring', label: 'Live Scoring', requiresAuth: false, onClick: handleLiveScoringClick }]
            : [];
        return [...base, ...liveScoring];
    }, [handleLiveScoringClick, windowSize.isMobile]);

    const isMobileLayout = windowSize.isMobile || windowSize.verticalPC !== 'none' || ['small'].includes(windowSize.horizontalPC);

    const handleMenuItemClick = (item: typeof menuItems[0]) => {
        if (item.requiresAuth && !isAuthenticated) return;
        if (item.matchName === 'leaderboards') setLeaderboardEvent(null);
        setTabValue(item.matchName);
        setEditEvent(null);
        setStandingsEvent(null);
        setSelectedTrip(null);
        setLeaderboardDrawerEvent(null);
        setMyEventsPopperEventId(null);
        setMyEventsPopperAnchorEl(null);
        if (isMobileLayout) setDrawerOpen(false);
    };

    const handleViewLeaderboard = useCallback(() => {
        if (editEvent?._id) {
            setLeaderboardEvent(editEvent);
            setTabValue('leaderboards');
            if (isMobileLayout) setDrawerOpen(false);
            if (isMobileView) handleCloseMobileDrawer();
        }
    }, [editEvent?._id, isMobileLayout, isMobileView, handleCloseMobileDrawer]);

    const SideNavContent = () => (
        <Stack
            direction={'column'}
            spacing={0}
            padding={'20px 0px'}
            alignItems={'center'}
            justifyContent={'space-between'}
            sx={{
                /* flex: 1, */
                height: '90%',
                minHeight: 0,
                overflow: 'hidden',
                borderRight: !isMobileLayout ? `1px solid ${appTheme.palette.primary.light}` : 'none',
                paddingRight: '1px',
            }}
        >
            <Stack direction={'column'} spacing={2} width={'95%'}
            sx={{ flexShrink: 0}}>
                {menuItems.map((item) => (
                    <MenuSpanHeader
                        key={item.matchName}
                        matchName={item.matchName}
                        label={item.label}
                        accountTabValue={tabValue}
                        setViewEvent={setEditEvent}
                        setAccountTabValue={'onClick' in item ? () => {} : () => handleMenuItemClick(item)}
                        onClick={'onClick' in item ? (item as { onClick: () => void }).onClick : undefined}
                        locked={item.requiresAuth && !isAuthenticated}
                        lockTooltip="Sign In to Access"
                    />
                ))}
            </Stack>
            <Stack direction='row' width={'70%'} spacing={2} justifyContent={'flex-start'} alignItems={'flex-start'}>
                <img src={SCRIPT_CURSIVE_LOGO} alt="Moment Golf" style={{ width: '100%', maxWidth: 80, objectFit: 'contain' }} />
            </Stack>
        </Stack>
    );

    const handleFindMissingEvents = async () => {
        const registrations = await api.get(`/myaccount/RegistrationNoUserMatchEmail?email=${userData.userEmail}`);
        setMissingEvents(registrations.data);
        setOpenMissingEvents(true);
    };

    const handleCloseMissingEvents = () => {
        setOpenMissingEvents(false);
        setMissingEvents([]);
    };

    const handleSyncEvents = async () => {
        const updatedEvents = missingEvents.filter((event: any) => event.claim);
        await Promise.all(updatedEvents.map((event: any) =>
            api.post('/myaccount/UpdateRegistration', {
                registrationID: event._id,
                userID: auth.user?.profile.sub
            })
        ));
        setOpenMissingEvents(false);
        setMissingEvents([]);
        await getRegistrations();
        const tripsRes = await api.get(`/mc/GetUserTrips?userID=${auth.user?.profile?.sub}`);
        setUserTrips(tripsRes.data || []);
    };

    const noShowKeys = ['_id', 'teeTimes', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy', 'modifiedAt', 'modifiedBy',
        'userID', 'eventID', 'registrationID', 'withdrawn', 'canceled', 'eventScoreID', 'partners', 'teamMembers',
        'hasEventScoreID', 'hasUserID'];

    const getRegistrationDisplayValue = (value: unknown): React.ReactNode => {
        if (value === true) return '✓';
        if (value != null && typeof value === 'object' && 'value' in value) {
            const nested = (value as { value?: unknown }).value;
            if (nested != null && nested !== '') return getRegistrationDisplayValue(nested);
        }
        if (value === null || value === undefined) return null;
        if (Array.isArray(value)) return value.length === 0 ? null : value.join(', ');
        if (typeof value === 'object') {
            const keys = Object.keys(value);
            return keys.length === 0 ? null : JSON.stringify(value);
        }
        return value as string | number;
    };

    const isBlankRegistrationValue = (value: unknown): boolean => {
        if (value === false) return true;
        const display = getRegistrationDisplayValue(value);
        if (display === null || display === undefined) return true;
        if (typeof display === 'string') return display.trim() === '';
        return false;
    };

    const formatEventDate = (event: any) => {
        const opts = { year: '2-digit' as const, month: 'numeric' as const, day: 'numeric' as const };
        if (new Date(event.startDate).toLocaleDateString('en-US', { timeZone: 'UTC' }) === new Date(event.endDate).toLocaleDateString('en-US', { timeZone: 'UTC' })) {
            return formatDateForDisplay(event.startDate, opts);
        }
        return formatDateForDisplay(event.startDate, opts) + ' - ' + formatDateForDisplay(event.endDate, opts);
    };

    const formatTripDateRange = (trip: any) => {
        const eventIds = trip?.eventIDs ?? [];
        const linkedEvents = userRegisteredEvents.events.filter((e: any) => eventIds.includes(String(e._id)));
        if (linkedEvents.length === 0) return '—';
        const minStart = Math.min(...linkedEvents.map((e: any) => new Date(e.startDate).getTime()));
        const maxEnd = Math.max(...linkedEvents.map((e: any) => new Date(e.endDate).getTime()));
        const opts = { year: '2-digit' as const, month: 'numeric' as const, day: 'numeric' as const };
        const startStr = formatDateForDisplay(new Date(minStart), opts);
        const endStr = formatDateForDisplay(new Date(maxEnd), opts);
        return startStr === endStr ? startStr : `${startStr} - ${endStr}`;
    };

    const isTripInFuture = (trip: any) => {
        const eventIds = trip?.eventIDs ?? [];
        const linkedEvents = userRegisteredEvents.events.filter((e: any) => eventIds.includes(String(e._id)));
        if (linkedEvents.length === 0) return false;
        const minStart = Math.min(...linkedEvents.map((e: any) => new Date(e.startDate).getTime()));
        const today = utcToLocalDateString(new Date());
        const startDateStr = utcToLocalDateString(new Date(minStart));
        return startDateStr > today;
    };

    const getTripCountdown = (trip: any) => {
        if (!isTripInFuture(trip)) return '';
        const eventIds = trip?.eventIDs ?? [];
        const linkedEvents = userRegisteredEvents.events.filter((e: any) => eventIds.includes(String(e._id)));
        if (linkedEvents.length === 0) return '';
        const minStart = Math.min(...linkedEvents.map((e: any) => new Date(e.startDate).getTime()));
        const today = new Date();
        today.setUTCHours(0, 0, 0, 0);
        const startDate = new Date(minStart);
        startDate.setUTCHours(0, 0, 0, 0);
        const days = Math.ceil((startDate.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
        if (days === 0) return 'Starts today';
        if (days === 1) return 'Starts tomorrow';
        return `${days} days until trip`;
    };

    const formatTeeTime = (teeTimes: any[]) => {
        if (!teeTimes || teeTimes.length === 0) return '';
        if (teeTimes.length === 1) {
            const t = teeTimes[0];
            const timeStr = new Date(t.teeTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
            return `Round ${t.round} - ${timeStr} | Hole ${t.startingHole ?? '-'}`;
        }
        return teeTimes.map((t: any) => `R${t.round}: ${new Date(t.teeTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' })}`).join(', ');
    };

    const formatEventTimeOrTBD = (event: any, userReg: any) => {
        if (userReg?.teeTimes?.length > 0) return formatTeeTime(userReg.teeTimes);
        const startTime = event?.startingTime;
        const timeStr = startTime
            ? new Date(`2000-01-01T${startTime}Z`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' })
            : null;
        return timeStr ? `Round 1 | ${timeStr} - Tee Time TBD` : 'Round 1 | Tee Time TBD';
    };

    const isEventUpcoming = (event: any) => new Date(event.endDate).getTime() + 24 * 60 * 60 * 1000 > Date.now();
    const getEventSortDate = (event: any, userReg: any) => {
        if (userReg?.teeTimes?.length) {
            const earliest = userReg.teeTimes.reduce((earliest: any, t: any) =>
                (!earliest || new Date(t.teeTime).getTime() < new Date(earliest.teeTime).getTime()) ? t : earliest);
            return new Date(earliest.teeTime).getTime();
        }
        return new Date(event.startDate).getTime();
    };

    const MyEventsInfoPopperContent = ({ event }: { event: any }) => {
        const loc = event?.courseInfo?.location;
        const boxSx = {
            p: 2,
            borderRadius: 2,
            backgroundColor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
        } as const;
        return (
            <Stack direction="column" spacing={2} sx={{ p: 2, minWidth: 260 }}>
                <Box sx={boxSx}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>Course Information</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>{event.courseName}</Typography>
                    {loc && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', wordBreak: 'break-word', mt: 0.5 }}>
                            {[loc.address, loc.city, `${loc.state || ''} ${loc.zip || ''}`.trim()].filter(Boolean).join(', ')}
                        </Typography>
                    )}
                </Box>
                <Box sx={boxSx}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>Event Information</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        {event.playFormat} — {event.playType}
                        {event.eventRegion && event.eventState && ` · ${event.eventRegion}, ${event.eventState}`}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.25fr 1.25fr 0.9fr', gap: '8px 24px', width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', flexShrink: 0 }}>Holes:</Typography>
                            <Typography variant="body2" color="text.secondary">{event.totalHoles}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', flexShrink: 0 }}>Rounds:</Typography>
                            <Typography variant="body2" color="text.secondary">{event.totalRounds}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', flexShrink: 0 }}>Field:</Typography>
                            <Typography variant="body2" color="text.secondary">{event.playerCap}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', flexShrink: 0 }}>Sign Up:</Typography>
                            <Typography variant="body2" color="text.secondary">{event.regType}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', flexShrink: 0 }}>Closes:</Typography>
                            <Typography variant="body2" color="text.secondary">{formatDateForDisplay(event.regCloseDate)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', flexShrink: 0 }}>Open:</Typography>
                            <Typography variant="body2" color="text.secondary">{event.playerCap - event.registrationCount}</Typography>
                        </Box>
                    </Box>
                </Box>
            </Stack>
        );
    };

    const ShowRegistration = () => (
        <Stack direction="column" sx={{ width: '100%', height: '100%', minHeight: 0 }}>
            <Typography id={'header-text'} variant="subtitle1"
                sx={{ fontWeight: 500, color: appTheme.palette.primary.main, textAlign: 'center', textIndent: 10, flexShrink: 0 }}>
                {editEvent ? `${editEvent.eventName} - Registration Details` : ''}
            </Typography>
            {!editEvent && (
                <Typography variant="body2" sx={{ color: appTheme.palette.text.secondary, textAlign: 'center', mt: 2, px: 2 }}>
                    Select an event to see the registration data
                </Typography>
            )}
            {editEvent && userRegisteredEvents?.registrations
                .filter((reg: any) => reg.eventID === editEvent._id && reg.userID === auth.user?.profile.sub)
                .map((registration: any) => (
                    <Stack key={registration._id} direction={'column'} spacing={1} sx={{ width: '100%', flex: 1, minHeight: 0, overflowY: 'auto' }}>
                        {Object.keys(registration)
                            .filter((key: any) => !noShowKeys.includes(key) && !isBlankRegistrationValue(registration[key]))
                            .map((key: any, index: number) => (
                                <Stack key={index} direction={'row'} gap={1} sx={{ width: '100%', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <Typography variant='subtitle2' sx={{ fontWeight: 500, textTransform: 'capitalize' }}>{key}:</Typography>
                                    <Typography variant='subtitle2' sx={{ fontWeight: 400 }}>
                                        {getRegistrationDisplayValue(registration[key])}
                                    </Typography>
                                </Stack>
                            ))}
                        {registration.teeTimes && registration.teeTimes.length > 0 && (
                            <><Divider />
                            <Stack direction={'column'} spacing={1} justifyContent={'flex-start'} alignItems={'flex-start'}>
                                <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>Tee Times:</Typography>
                                {registration.teeTimes.map((teeTime: any) => (
                                    <Typography key={teeTime.round} variant='subtitle2' sx={{ fontWeight: 400 }}>
                                        Round {teeTime.round} - {new Date(teeTime.teeTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' })} | Hole: {teeTime.startingHole}
                                    </Typography>
                                ))}
                            </Stack></>
                        )}
                        {registration.partners && registration.partners.length > 0 && (
                            <><Divider />
                            <Stack direction={'column'} spacing={1} justifyContent={'flex-start'} alignItems={'flex-start'}>
                                <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>Partners:</Typography>
                                {registration.partners.map((partner: any) => (
                                    <Typography key={partner._id} variant='subtitle2' sx={{ fontWeight: 400 }}>{partner.playerName}</Typography>
                                ))}
                            </Stack></>
                        )}
                        {editEvent.useTeams && registration.teamMembers && registration.teamMembers.length > 0 && (
                            <><Divider />
                            <Stack direction={'column'} spacing={1} justifyContent={'flex-start'} alignItems={'flex-start'}>
                                <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>Team Members:</Typography>
                                {registration.teamMembers.map((member: any) => (
                                    <Typography key={member._id} variant='subtitle2' sx={{ fontWeight: 400 }}>{member.playerName}</Typography>
                                ))}
                            </Stack></>
                        )}
                        <><Divider />
                        <Stack direction={'column'} spacing={1} justifyContent={'flex-start'} alignItems={'flex-start'}>
                            <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>Logistical Information:</Typography>
                            <Stack direction={'row'} spacing={1} sx={{ alignItems: 'flex-start' }}>
                                <Typography variant='subtitle2' sx={{ fontWeight: 400 }}>Status:</Typography>
                                <Stack direction={'row'} spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                                    {registration.canceled && <Chip label="Canceled" sx={{ height: 20, fontSize: 12, backgroundColor: 'error.light', color: 'white' }} />}
                                    {registration.withdrawn && <Chip label="Withdrawn" sx={{ height: 20, fontSize: 12, backgroundColor: 'rgb(150, 100, 255)', color: 'white' }} />}
                                    {registration.disqualified && <Chip label="Disqualified" sx={{ height: 20, fontSize: 12, backgroundColor: 'warning.light', color: 'white' }} />}
                                    {registration.refunded && <Chip label="Refunded" sx={{ height: 20, fontSize: 12, backgroundColor: 'info.main', color: 'white' }} />}
                                    {!registration.canceled && !registration.withdrawn && !registration.disqualified && !registration.refunded && (
                                        <Chip label="Active" sx={{ height: 20, fontSize: 12, backgroundColor: 'success.light', color: 'white' }} />
                                    )}
                                </Stack>
                            </Stack>
                            {registration.refundDate && <Typography variant='subtitle2' sx={{ fontWeight: 400 }}>Refunded Date: {formatDateForDisplay(registration.refundDate)}</Typography>}
                            {registration.refundAmount && <Typography variant='subtitle2' sx={{ fontWeight: 400 }}>Refunded Amount: ${registration.refundAmount}</Typography>}
                            {registration.modifiedAt && <Typography variant='subtitle2' sx={{ fontWeight: 400 }}>Last Modified: {new Date(registration.modifiedAt).toLocaleString()}</Typography>}
                        </Stack></>
                    </Stack>
                ))}
        </Stack>
    );

    const DisplayMissingEvents = () => (
        <Drawer variant='temporary' anchor='right' open={openMissingEvents} onClose={handleCloseMissingEvents}
            hideBackdrop={true}
            sx={{ '& .MuiDrawer-paper': { minWidth: 250, maxWidth: 400, width: '70%', height: '100%', padding: 2 } }}
        >
            <Stack direction={'column'} spacing={2} alignContent={'center'}
                height={`calc(100dvh - ${60 * factor}px - env(safe-area-inset-bottom))`}>
                {missingEvents.length > 0 &&
                    <><Stack direction={'column'} spacing={2} width={'100%'}>
                        <Typography variant="h6" flex={1} sx={{ textAlign: 'center', fontWeight: 500, color: appTheme.palette.primary.main }}>Syncing Found Events</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: appTheme.palette.primary.main }}>
                            The following events were found with your email address, but are not currently linked to any account.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: appTheme.palette.primary.main }}>
                            Please select the events you would like to link to your account.
                        </Typography>
                    </Stack>
                    <Stack direction={'column'} spacing={2} width={'100%'}>
                        {missingEvents.map((event: any) => (
                            <Stack key={event._id} direction={'row'} spacing={2} width={'100%'} alignItems={'center'}>
                                <Checkbox checked={event?.claim} onChange={(e) => {
                                    e.stopPropagation();
                                    const checked = e.target.checked;
                                    setMissingEvents(missingEvents.map((me: any) =>
                                        me._id === event._id ? { ...me, claim: checked } : me
                                    ));
                                }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: appTheme.palette.primary.main }}>{event.eventName}</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: appTheme.palette.primary.main }}>{event.startDate}</Typography>
                            </Stack>
                        ))}
                    </Stack></>
                }
                {missingEvents.length === 0 &&
                    <><Typography variant="h6" width={'100%'} sx={{ textAlign: 'center', fontWeight: 500, color: appTheme.palette.primary.main }}>No Unlinked Events Found</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500, color: appTheme.palette.primary.main }}>
                        If you are still missing an event, please reach out to the event coordinator or course.
                    </Typography></>
                }
                <Stack direction={'row'} spacing={2} width={'100%'} justifyContent={'flex-end'}>
                    {missingEvents.length > 0 && <Button variant="contained" color="primary" onClick={handleSyncEvents}>Claim Events</Button>}
                    <Button variant="contained" color="error" onClick={handleCloseMissingEvents}>Close</Button>
                </Stack>
            </Stack>
        </Drawer>
    );

    return (
        <Stack direction='column' width='100dvw' height='100dvh'
            sx={{ WebkitOverflowScrolling: 'touch', WebkitOverscrollBehavior: 'none' }}
        >
            <PullToRefresh threshold={80} resistance={2} containerRef={stackRef as React.RefObject<HTMLDivElement>}
                onRefresh={() => { navigate(0); return Promise.resolve(); }}>

                <MCHeader label='Moment Central' showCart={true} showMarquee={true} hamburgerFabVisible={isMobileLayout} />

                {isMobileLayout && (
                    <Fab
                        onClick={() => setDrawerOpen(true)}
                        sx={{
                            position: 'absolute',
                            top: 10 * factor,
                            left: 10 * factor,
                            zIndex: 1200,
                            width: 'max-content',
                            height: 'max-content',
                            backgroundColor: 'transparent',
                            border: 'none',
                            boxShadow: 'none',
                        }}
                    >
                        <MenuIcon id="drawer-menu-icon"
                            sx={{ fontSize: 24 * factor, borderRadius: '50%', padding: '8px', backgroundColor: 'primary.light', color: 'white', boxShadow: 'none' }} />
                    </Fab>
                )}

                {isMobileLayout && (
                    <Drawer anchor='left' variant='temporary' open={drawerOpen} onClose={() => setDrawerOpen(false)}
                        sx={{
                            '& .MuiDrawer-paper': {
                                width: 200,
                                boxSizing: 'border-box',
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                maxHeight: '100dvh',
                            },
                        }}
                    >
                        <Stack direction={'column'} spacing={0} flex={1} sx={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
                            <SideNavContent />
                        </Stack>
                    </Drawer>
                )}

                <Stack direction={'row'} spacing={2} flex={1} sx={isMobileLayout ? { width: '100%', minWidth: 0 } : undefined}>
                    {!isMobileLayout && (
                        <Stack direction={'column'} spacing={0} width={200} flexShrink={0}
                            sx={{
                                height: `calc(100dvh - ${60 * factor}px - env(safe-area-inset-bottom))`,
                                overflow: 'hidden',
                                display: 'flex',
                            }}
                        >
                            <SideNavContent />
                        </Stack>
                    )}

                    {/* Home tab */}
                    {tabValue === 'home' && (
                        <MCHomePage onBrowseEvents={() => setTabValue('search')} />
                    )}

                    {/* Search tab */}
                    {tabValue === 'search' && (
                        <Stack
                            direction='column'
                            flex={1}
                            sx={{
                                backgroundColor: 'white',
                                borderRadius: 2,
                                minWidth: 0,
                                maxHeight: `calc(100dvh - ${120 * factor}px - env(safe-area-inset-bottom))`,
                                overflow: 'hidden',
                            }}
                        >
                            <PriceDisplayModeBar />
                            <Stack
                                direction='column'
                                spacing={2 * factor}
                                flex={1}
                                sx={{
                                    padding: `${10 * factor}px ${20 * factor}px`,
                                    paddingTop: `${12 * factor}px`,
                                    justifyContent: 'flex-start',
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    minHeight: 0,
                                    WebkitOverflowScrolling: 'touch',
                                    '&::-webkit-scrollbar': { display: 'none' }
                                }}
                            >
                                <EventCardSection togglePastEvents={true} stackRef={stackRef} />
                            </Stack>
                        </Stack>
                    )}

                    {/* Leaderboards tab */}
                    {tabValue === 'leaderboards' && process.env.REACT_APP_LEADERBOARD_URL && (
                        <Box
                            flex={1}
                            sx={{
                                minHeight: 0,
                                minWidth: 0,
                                width: isMobileLayout ? '100%' : undefined,
                                height: isMobileLayout ? `calc(100dvh - ${120 * factor}px - env(safe-area-inset-bottom))` : undefined,
                                overflow: 'hidden',
                                position: 'relative',
                            }}
                        >
                            <iframe
                                src={`${process.env.REACT_APP_LEADERBOARD_URL}?headless=true&autoSelectFirst=true`}
                                title="Leaderboard"
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    border: 'none',
                                }}
                            />
                        </Box>
                    )}

                    {/* Standings tab */}
                    {tabValue === 'standings' && (
                        <MCStandingsSection />
                    )}

                    {/* My Events tab */}
                    {tabValue === 'events' && (
                        <><Stack direction={'column'} spacing={2} flex={3} ref={stackRef}
                            sx={{
                                padding: 3,
                                overflow: 'hidden',
                                alignItems: 'center',
                                maxHeight: `calc(100dvh - ${120 * factor}px - env(safe-area-inset-bottom))`,
                                minHeight: 0,
                            }}
                        >
                            <Stack direction="row" spacing={2} sx={{ width: '100%', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                                <Typography variant="h6" sx={{ fontWeight: 500, color: appTheme.palette.primary.main }}>My Events</Typography>
                                <Stack direction={'row'} spacing={2} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                                    <Button variant="contained" color="primary" onClick={handleFindMissingEvents}>Sync Events</Button>
                                </Stack>
                                <DisplayMissingEvents />
                            </Stack>

                            <Stack
                                direction={!isMobileView ? 'column' : 'row'}
                                flexWrap={!isMobileView ? 'nowrap' : 'wrap'}
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    gap: 2 * factor,
                                    justifyContent: !isMobileView ? 'flex-start' : 'center',
                                    alignItems: !isMobileView ? 'stretch' : 'flex-start',
                                    alignContent: 'flex-start',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    WebkitOverflowScrolling: 'touch',
                                    '&::-webkit-scrollbar': { display: 'none' },
                                }}
                            >
                                {userRegisteredEvents.events.length === 0 && (
                                    <Typography variant="subtitle1" sx={{ fontWeight: 500, color: appTheme.palette.primary.main }}>
                                        No events found...
                                    </Typography>
                                )}
                                {(() => {
                                    const upcoming = userRegisteredEvents.events
                                        .filter((e: any) => isEventUpcoming(e))
                                        .sort((a: any, b: any) => {
                                            const regA = userRegisteredEvents?.registrations?.find((r: any) => r.eventID === a._id && r.userID === auth.user?.profile?.sub);
                                            const regB = userRegisteredEvents?.registrations?.find((r: any) => r.eventID === b._id && r.userID === auth.user?.profile?.sub);
                                            return getEventSortDate(a, regA) - getEventSortDate(b, regB);
                                        });
                                    const completed = userRegisteredEvents.events
                                        .filter((e: any) => !isEventUpcoming(e))
                                        .sort((a: any, b: any) => {
                                            const regA = userRegisteredEvents?.registrations?.find((r: any) => r.eventID === a._id && r.userID === auth.user?.profile?.sub);
                                            const regB = userRegisteredEvents?.registrations?.find((r: any) => r.eventID === b._id && r.userID === auth.user?.profile?.sub);
                                            return getEventSortDate(b, regB) - getEventSortDate(a, regA);
                                        });
                                    return (
                                        <>
                                            {upcoming.length > 0 && (
                                                <>
                                                    <Stack direction="column" spacing={0.25} sx={{ width: '100%', alignItems: 'flex-start' }}>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: appTheme.palette.primary.main, textAlign: 'left' }}>Upcoming</Typography>
                                                        <Divider sx={{ width: '100%', borderColor: 'divider' }} />
                                                    </Stack>
                                                    {upcoming.map((item: any) => {
                                        const userReg = userRegisteredEvents?.registrations?.find((r: any) => r.eventID === item._id && r.userID === auth.user?.profile?.sub);
                                        const imageWidth = isMobileLayout ? 100 * factor : 120 * factor;
                                        const isMobile = isMobileLayout;
                                        const isSingleColumn = !isMobileView;
                                        return (
                                            <Box key={item._id} sx={{
                                                flex: isSingleColumn ? '0 0 auto' : (isMobile ? '1 1 100%' : '1 1 0%'),
                                                minWidth: isMobile ? 280 : 280,
                                                maxWidth: isMobile || isSingleColumn ? '100%' : 550,
                                                width: isSingleColumn ? '100%' : undefined,
                                                boxSizing: 'border-box',
                                            }}>
                                                <Paper
                                                    elevation={2}
                                                    sx={{
                                                        width: '100%',
                                                        padding: 1.5 * factor,
                                                        border: (editEvent?._id === item._id || standingsEvent?._id === item._id)
                                                            ? `2px solid ${appTheme.palette.success.main}`
                                                            : `1px solid ${appTheme.palette.secondary.main}`,
                                                        position: 'relative',
                                                        overflow: 'hidden',
                                                        boxSizing: 'border-box',
                                                        transition: 'all 0.3s ease-in-out',
                                                        opacity: transitioning ? 0.5 : 1,
                                                    }}
                                                    onClick={() => handleEventClick(item)}
                                                    onTransitionEnd={() => setTransitioning(false)}
                                                >
                                                    <Stack direction="column" spacing={1.5}>
                                                        {/* Top row: Tags + Info icon + HowToReg icon */}
                                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', minHeight: 28 }}>
                                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ flex: 1, minWidth: 0, gap: 0.5 }}>
                                                                {userReg?.refunded && <Chip key="refunded" label="Refunded" size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: 'info.main', color: 'white' }} />}
                                                                {userReg?.canceled && <Chip key="canceled" label="Canceled" size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: 'error.light', color: 'white' }} />}
                                                                {userReg?.withdrawn && !userReg?.canceled && <Chip key="withdrawn" label="Withdrawn" size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: 'rgb(150, 100, 255)', color: 'white' }} />}
                                                                {userReg?.disqualified && !userReg?.canceled && <Chip key="disqualified" label="Disqualified" size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: 'warning.light', color: 'white' }} />}
                                                                {userReg && !userReg.refunded && !userReg.canceled && !userReg.withdrawn && !userReg.disqualified && (
                                                                    new Date(item.endDate).getTime() + 24 * 60 * 60 * 1000 > new Date().getTime()
                                                                        ? <Chip key="active" label="Active" size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: 'success.light', color: 'white' }} />
                                                                        : <Chip key="completed" label="Completed" size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: 'info.main', color: 'white' }} />
                                                                )}
                                                                {item.tags?.map((tag: string, idx: number) => (
                                                                    <Chip key={`tag-${idx}`} label={tag} size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: appTheme.palette.primary.light, color: 'white' }} />
                                                                ))}
                                                            </Stack>
                                                            {process.env.REACT_APP_LEADERBOARD_URL && (
                                                                <IconButton size="small" onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (isMobileView) {
                                                                        setLeaderboardDrawerEvent(item);
                                                                        setEditEvent(null);
                                                                        setMyEventsPopperEventId(null);
                                                                        setMyEventsPopperAnchorEl(null);
                                                                    } else {
                                                                        setStandingsEvent(item);
                                                                        setEditEvent(null);
                                                                    }
                                                                }} sx={{ color: appTheme.palette.primary.main, flexShrink: 0 }} title="Standings"><LeaderboardIcon fontSize="small" /></IconButton>
                                                            )}
                                                            <IconButton size="small" onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (isMobileView) {
                                                                    setMyEventsPopperEventId(myEventsPopperEventId === item._id ? null : item._id);
                                                                    setEditEvent(null);
                                                                    setLeaderboardDrawerEvent(null);
                                                                } else {
                                                                    setMyEventsPopperAnchorEl(myEventsPopperEventId === item._id ? null : e.currentTarget);
                                                                    setMyEventsPopperEventId(myEventsPopperEventId === item._id ? null : item._id);
                                                                }
                                                            }} sx={{ color: appTheme.palette.primary.main, flexShrink: 0 }}><InfoOutlinedIcon fontSize="small" /></IconButton>
                                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEventClick(item); }} sx={{ color: appTheme.palette.primary.main, flexShrink: 0 }} title="Registration details"><HowToRegIcon fontSize="small" /></IconButton>
                                                        </Stack>
                                                        {/* Body row: Image left | Title, Course, Date, Tee time right */}
                                                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, overflow: 'hidden' }}>
                                                            <Stack direction="column" sx={{ flexShrink: 0, alignItems: 'center' }}>
                                                                <Box
                                                                    sx={{
                                                                        maxWidth: imageWidth,
                                                                        maxHeight: isMobile ? 80 * factor : 100 * factor,
                                                                        overflow: 'hidden',
                                                                        borderRadius: 2.5,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                    }}
                                                                >
                                                                    <img
                                                                        src={item.eventCardImageURL ? (imageCache[item.eventCardImageURL] || item.eventCardImageURL) : QUILTED_FLAG_LOGO}
                                                                        alt={item.eventName}
                                                                        style={{
                                                                            maxWidth: '100%',
                                                                            maxHeight: isMobile ? 80 * factor : 100 * factor,
                                                                            width: 'auto',
                                                                            height: 'auto',
                                                                            display: 'block',
                                                                            verticalAlign: 'middle',
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </Stack>
                                                            <Stack direction="column" spacing={1} sx={{ flex: 1, minWidth: 0, overflow: 'hidden', alignItems: isMobile ? 'center' : 'stretch' }}>
                                                                <Stack direction="column" spacing={0.5} sx={{ alignItems: isMobile ? 'center' : 'flex-start', width: '100%' }}>
                                                                    <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600, textAlign: isMobile ? 'center' : 'left', overflow: 'hidden', wordBreak: 'break-word', minWidth: 0, fontSize: isMobile ? '0.95rem' : undefined }}>{item.eventName}</Typography>
                                                                    <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} sx={{ fontWeight: 500, textAlign: isMobile ? 'center' : 'left', overflow: 'hidden', wordBreak: 'break-word', minWidth: 0, fontSize: isMobile ? '0.8rem' : undefined }}>@ {item.courseName}</Typography>
                                                                    {isMobile ? (
                                                                        <Stack direction="column" spacing={0.25} alignItems="center">
                                                                            <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: '0.75rem', textAlign: 'center' }}>{formatEventDate(item)}</Typography>
                                                                            {userReg && !userReg.refunded && !userReg.canceled && !userReg.withdrawn && !userReg.disqualified && <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: '0.75rem', textAlign: 'center' }}>{formatEventTimeOrTBD(item, userReg)}</Typography>}
                                                                        </Stack>
                                                                    ) : (
                                                                        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ gap: 0.5 }}>
                                                                            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>{formatEventDate(item)}</Typography>
                                                                            {userReg && !userReg.refunded && !userReg.canceled && !userReg.withdrawn && !userReg.disqualified && <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>{formatEventTimeOrTBD(item, userReg)}</Typography>}
                                                                        </Stack>
                                                                    )}
                                                                </Stack>
                                                            </Stack>
                                                        </Stack>
                                                    </Stack>
                                                </Paper>
                                            </Box>
                                        );
                                                    })}
                                                </>
                                            )}
                                            {completed.length > 0 && (
                                                <>
                                                    <Stack direction="column" spacing={0.25} sx={{ width: '100%', alignItems: 'flex-start' }}>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: appTheme.palette.primary.main, textAlign: 'left' }}>Completed</Typography>
                                                        <Divider sx={{ width: '100%', borderColor: 'divider' }} />
                                                    </Stack>
                                                    {completed.map((item: any) => {
                                                        const userReg = userRegisteredEvents?.registrations?.find((r: any) => r.eventID === item._id && r.userID === auth.user?.profile?.sub);
                                                        const imageWidth = isMobileLayout ? 100 * factor : 120 * factor;
                                                        const isMobile = isMobileLayout;
                                                        const isSingleColumn = !isMobileView;
                                                        return (
                                                            <Box key={item._id} sx={{
                                                                flex: isSingleColumn ? '0 0 auto' : (isMobile ? '1 1 100%' : '1 1 0%'),
                                                                minWidth: isMobile ? 280 : 280,
                                                                maxWidth: isMobile || isSingleColumn ? '100%' : 550,
                                                                width: isSingleColumn ? '100%' : undefined,
                                                                boxSizing: 'border-box',
                                                            }}>
                                                                <Paper
                                                                    elevation={2}
                                                                    sx={{
                                                                        width: '100%',
                                                                        padding: 1.5 * factor,
                                                                        border: (editEvent?._id === item._id || standingsEvent?._id === item._id)
                                                                            ? `2px solid ${appTheme.palette.success.main}`
                                                                            : `1px solid ${appTheme.palette.secondary.main}`,
                                                                        position: 'relative',
                                                                        overflow: 'hidden',
                                                                        boxSizing: 'border-box',
                                                                        transition: 'all 0.3s ease-in-out',
                                                                        opacity: transitioning ? 0.5 : 1,
                                                                    }}
                                                                    onClick={() => handleEventClick(item)}
                                                                    onTransitionEnd={() => setTransitioning(false)}
                                                                >
                                                                    <Stack direction="column" spacing={1.5}>
                                                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', minHeight: 28 }}>
                                                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ flex: 1, minWidth: 0, gap: 0.5 }}>
                                                                                {userReg?.refunded && <Chip key="refunded" label="Refunded" size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: 'info.main', color: 'white' }} />}
                                                                                {userReg?.canceled && <Chip key="canceled" label="Canceled" size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: 'error.light', color: 'white' }} />}
                                                                                {userReg?.withdrawn && !userReg?.canceled && <Chip key="withdrawn" label="Withdrawn" size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: 'rgb(150, 100, 255)', color: 'white' }} />}
                                                                                {userReg?.disqualified && !userReg?.canceled && <Chip key="disqualified" label="Disqualified" size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: 'warning.light', color: 'white' }} />}
                                                                                {userReg && !userReg.refunded && !userReg.canceled && !userReg.withdrawn && !userReg.disqualified && (
                                                                                    new Date(item.endDate).getTime() + 24 * 60 * 60 * 1000 > new Date().getTime()
                                                                                        ? <Chip key="active" label="Active" size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: 'success.light', color: 'white' }} />
                                                                                        : <Chip key="completed" label="Completed" size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: 'info.main', color: 'white' }} />
                                                                                )}
                                                                                {item.tags?.map((tag: string, idx: number) => (
                                                                                    <Chip key={`tag-${idx}`} label={tag} size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: appTheme.palette.primary.light, color: 'white' }} />
                                                                                ))}
                                                                            </Stack>
                                                                            {process.env.REACT_APP_LEADERBOARD_URL && (
                                                                                <IconButton size="small" onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    if (isMobileView) {
                                                                                        setLeaderboardDrawerEvent(item);
                                                                                        setEditEvent(null);
                                                                                        setMyEventsPopperEventId(null);
                                                                                        setMyEventsPopperAnchorEl(null);
                                                                                    } else {
                                                                                        setStandingsEvent(item);
                                                                                        setEditEvent(null);
                                                                                    }
                                                                                }} sx={{ color: appTheme.palette.primary.main, flexShrink: 0 }} title="Standings"><LeaderboardIcon fontSize="small" /></IconButton>
                                                                            )}
                                                                            <IconButton size="small" onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                if (isMobileView) {
                                                                                    setMyEventsPopperEventId(myEventsPopperEventId === item._id ? null : item._id);
                                                                                    setEditEvent(null);
                                                                                    setLeaderboardDrawerEvent(null);
                                                                                } else {
                                                                                    setMyEventsPopperAnchorEl(myEventsPopperEventId === item._id ? null : e.currentTarget);
                                                                                    setMyEventsPopperEventId(myEventsPopperEventId === item._id ? null : item._id);
                                                                                }
                                                                            }} sx={{ color: appTheme.palette.primary.main, flexShrink: 0 }}><InfoOutlinedIcon fontSize="small" /></IconButton>
                                                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEventClick(item); }} sx={{ color: appTheme.palette.primary.main, flexShrink: 0 }} title="Registration details"><HowToRegIcon fontSize="small" /></IconButton>
                                                                        </Stack>
                                                                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, overflow: 'hidden' }}>
                                                                            <Stack direction="column" sx={{ flexShrink: 0, alignItems: 'center' }}>
                                                                                <Box
                                                                                    sx={{
                                                                                        maxWidth: imageWidth,
                                                                                        maxHeight: isMobile ? 80 * factor : 100 * factor,
                                                                                        overflow: 'hidden',
                                                                                        borderRadius: 2.5,
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                    }}
                                                                                >
                                                                                    <img
                                                                                        src={item.eventCardImageURL ? (imageCache[item.eventCardImageURL] || item.eventCardImageURL) : QUILTED_FLAG_LOGO}
                                                                                        alt={item.eventName}
                                                                                        style={{
                                                                                            maxWidth: '100%',
                                                                                            maxHeight: isMobile ? 80 * factor : 100 * factor,
                                                                                            width: 'auto',
                                                                                            height: 'auto',
                                                                                            display: 'block',
                                                                                            verticalAlign: 'middle',
                                                                                        }}
                                                                                    />
                                                                                </Box>
                                                                            </Stack>
                                                                            <Stack direction="column" spacing={1} sx={{ flex: 1, minWidth: 0, overflow: 'hidden', alignItems: isMobile ? 'center' : 'stretch' }}>
                                                                                <Stack direction="column" spacing={0.5} sx={{ alignItems: isMobile ? 'center' : 'flex-start', width: '100%' }}>
                                                                                    <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600, textAlign: isMobile ? 'center' : 'left', overflow: 'hidden', wordBreak: 'break-word', minWidth: 0, fontSize: isMobile ? '0.95rem' : undefined }}>{item.eventName}</Typography>
                                                                                    <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} sx={{ fontWeight: 500, textAlign: isMobile ? 'center' : 'left', overflow: 'hidden', wordBreak: 'break-word', minWidth: 0, fontSize: isMobile ? '0.8rem' : undefined }}>@ {item.courseName}</Typography>
                                                                                    {isMobile ? (
                                                                                        <Stack direction="column" spacing={0.25} alignItems="center">
                                                                                            <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: '0.75rem', textAlign: 'center' }}>{formatEventDate(item)}</Typography>
                                                                                            {userReg && !userReg.refunded && !userReg.canceled && !userReg.withdrawn && !userReg.disqualified && <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: '0.75rem', textAlign: 'center' }}>{formatEventTimeOrTBD(item, userReg)}</Typography>}
                                                                                        </Stack>
                                                                                    ) : (
                                                                                        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ gap: 0.5 }}>
                                                                                            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>{formatEventDate(item)}</Typography>
                                                                                            {userReg && !userReg.refunded && !userReg.canceled && !userReg.withdrawn && !userReg.disqualified && <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>{formatEventTimeOrTBD(item, userReg)}</Typography>}
                                                                                        </Stack>
                                                                                    )}
                                                                                </Stack>
                                                                            </Stack>
                                                                        </Stack>
                                                                    </Stack>
                                                                </Paper>
                                                            </Box>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </>
                                    );
                                })()}
                            </Stack>
                        </Stack>

                        <Popover
                            open={!isMobileView && !!myEventsPopperAnchorEl && !!myEventsPopperEventId}
                            anchorEl={myEventsPopperAnchorEl}
                            onClose={() => { setMyEventsPopperAnchorEl(null); setMyEventsPopperEventId(null); }}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            slotProps={{ paper: { elevation: 4 } }}
                            sx={{ zIndex: 1300 }}
                            disableScrollLock
                        >
                            {myEventsPopperEventId && (() => {
                                const event = userRegisteredEvents.events.find((e: any) => e._id === myEventsPopperEventId);
                                return event ? <MyEventsInfoPopperContent event={event} /> : null;
                            })()}
                        </Popover>

                        {isMobileView && (!!editEvent || !!leaderboardDrawerEvent || !!myEventsPopperEventId) && (
                            <Drawer
                                anchor="bottom"
                                variant="temporary"
                                open={true}
                                onClose={handleCloseMobileDrawer}
                                transitionDuration={2500}
                                sx={{
                                    '& .MuiDrawer-paper': {
                                        height: (editEvent || leaderboardDrawerEvent) ? '90vh' : '60vh',
                                        width: '95vw',
                                        left: '50%',
                                        marginLeft: '-47.5vw',
                                        maxHeight: (editEvent || leaderboardDrawerEvent) ? '90vh' : '60vh',
                                        borderTopLeftRadius: 8,
                                        borderTopRightRadius: 8,
                                        padding: 2,
                                        paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
                                        boxSizing: 'border-box',
                                        transition: 'height 2500ms ease-in-out',
                                    },
                                }}
                            >
                                <Stack direction="column" sx={{ height: '100%', width: '100%', overflow: 'hidden', minHeight: 0 }}>
                                    <Stack direction="row" justifyContent="flex-end" padding={0.5} sx={{ flexShrink: 0 }}>
                                        <IconButton size="small" onClick={handleCloseMobileDrawer} sx={{ color: appTheme.palette.primary.main }} title="Close"><CloseIcon fontSize="small" /></IconButton>
                                    </Stack>
                                    <Box sx={{ flex: 1, minHeight: 0, overflow: editEvent ? 'auto' : 'hidden' }}>
                                        {editEvent ? (
                                            <ShowRegistration />
                                        ) : leaderboardDrawerEvent ? (
                                            <LeaderboardView event={leaderboardDrawerEvent} headless hideSelector />
                                        ) : myEventsPopperEventId ? (
                                            (() => {
                                                const event = userRegisteredEvents.events.find((e: any) => e._id === myEventsPopperEventId);
                                                return event ? <MyEventsInfoPopperContent event={event} /> : null;
                                            })()
                                        ) : null}
                                    </Box>
                                    {editEvent && (
                                        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" paddingTop={2} flexShrink={0} sx={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
                                            <Button variant="outlined" color="primary" onClick={handleViewLeaderboard}>View Leaderboard</Button>
                                            <Button variant="contained" color="primary" onClick={handleCloseMobileDrawer}>Close</Button>
                                        </Stack>
                                    )}
                                </Stack>
                            </Drawer>
                        )}

                        {!isMobileView && (
                            <Stack direction={'column'} spacing={0} flex={2} minWidth={280}
                                sx={{
                                    padding: `${5 * factor}px ${20 * factor}px`,
                                    paddingBottom: `max(${6 * factor}px, env(safe-area-inset-bottom))`,
                                    boxShadow: 4,
                                    transition: 'all 0.3s ease-in-out',
                                    opacity: transitioning ? 0.5 : 1,
                                    overflow: 'hidden',
                                    maxHeight: `calc(100dvh - ${100 * factor}px - env(safe-area-inset-bottom))`,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Stack display={standingsEvent ? 'flex' : 'none'} direction="row" justifyContent="flex-end" padding={0.5} sx={{ flexShrink: 0 }}>
                                    <IconButton size="small" onClick={() => setStandingsEvent(null)} sx={{ color: appTheme.palette.primary.main }} title="Close"><CloseIcon fontSize="small" /></IconButton>
                                </Stack>
                                <Box sx={{ flex: 1, minHeight: 0, overflow: standingsEvent ? 'hidden' : 'auto', WebkitOverflowScrolling: 'touch', '&::-webkit-scrollbar': { display: 'none' } }}>
                                    {standingsEvent ? (
                                        <LeaderboardView event={standingsEvent} headless hideSelector />
                                    ) : (
                                        <ShowRegistration />
                                    )}
                                </Box>
                                <Stack display={editEvent ? 'flex' : 'none'} direction={'row'} spacing={1} sx={{ flexShrink: 0, justifyContent: 'flex-end', alignItems: 'center', paddingTop: '5px' }}> 
                                    <Button variant="outlined" color="primary" onClick={handleViewLeaderboard}>View Leaderboard</Button>
                                    <Button variant="contained" color="primary" onClick={handleClose}>Close</Button>
                                </Stack>
                            </Stack>
                        )}
                        </>
                    )}

                    {/* My Trips tab */}
                    {tabValue === 'trips' && (
                        <>
                            {selectedTrip ? (
                                <>
                                    <Stack direction={'column'} spacing={2} flex={3} ref={stackRef}
                                        sx={{
                                            padding: 3,
                                            overflow: 'hidden',
                                            alignItems: 'center',
                                            maxHeight: `calc(100dvh - ${120 * factor}px - env(safe-area-inset-bottom))`,
                                            minHeight: 0,
                                        }}
                                    >
                                        {/* Trip detail header */}
                                        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ width: '100%', flexShrink: 0 }}>
                                        <Stack direction="column" spacing={0} alignItems="center" sx={{ flexShrink: 0 }}>
                                            <IconButton size="small" onClick={() => setSelectedTrip(null)} sx={{ color: appTheme.palette.primary.main, padding: 0.25 }} title="Back to trips">
                                                <ChevronLeftIcon fontSize="small" />
                                            </IconButton>
                                            <Typography variant="caption" sx={{ fontWeight: 500, color: appTheme.palette.primary.main, lineHeight: 1.2 }}>My trips</Typography>
                                        </Stack>
                                        <Stack direction="column" spacing={0.25} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center' }}>{selectedTrip.groupName}</Typography>
                                            <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
                                                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>{formatTripDateRange(selectedTrip)}</Typography>
                                                {(selectedTrip.city || selectedTrip.state) && (
                                                    <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                                                        {[selectedTrip.city, selectedTrip.state].filter(Boolean).join(', ')}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </Stack>

                                    {/* Scrollable My Events cards - filtered to trip events */}
                                    <Stack
                                        direction={!isMobileView ? 'column' : 'row'}
                                        flexWrap={!isMobileView ? 'nowrap' : 'wrap'}
                                        sx={{
                                            flex: 1,
                                            minHeight: 0,
                                            overflowY: 'auto',
                                            overflowX: 'hidden',
                                            gap: 2 * factor,
                                            justifyContent: !isMobileView ? 'flex-start' : 'center',
                                            alignItems: !isMobileView ? 'stretch' : 'flex-start',
                                            alignContent: 'flex-start',
                                            width: '100%',
                                            boxSizing: 'border-box',
                                            WebkitOverflowScrolling: 'touch',
                                            '&::-webkit-scrollbar': { display: 'none' },
                                        }}
                                    >
                                        {userRegisteredEvents.events
                                            .filter((item: any) => (selectedTrip.eventIDs || []).includes(String(item._id)))
                                            .sort((a: any, b: any) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
                                            .map((item: any) => {
                                                const userReg = userRegisteredEvents?.registrations?.find((r: any) => r.eventID === item._id && r.userID === auth.user?.profile?.sub);
                                                const imageWidth = isMobileLayout ? 100 * factor : 120 * factor;
                                                const isMobile = isMobileLayout;
                                                const isSingleColumn = !isMobileView;
                                                return (
                                                    <Box key={item._id} sx={{
                                                        flex: isSingleColumn ? '0 0 auto' : (isMobile ? '1 1 100%' : '1 1 0%'),
                                                        minWidth: isMobile ? 280 : 280,
                                                        maxWidth: isMobile || isSingleColumn ? '100%' : 550,
                                                        width: isSingleColumn ? '100%' : undefined,
                                                        boxSizing: 'border-box',
                                                    }}>
                                                        <Paper
                                                            elevation={2}
                                                            sx={{
                                                                width: '100%',
                                                                padding: 1.5 * factor,
                                                                border: (editEvent?._id === item._id || standingsEvent?._id === item._id)
                                                                    ? `2px solid ${appTheme.palette.success.main}`
                                                                    : `1px solid ${appTheme.palette.secondary.main}`,
                                                                position: 'relative',
                                                                overflow: 'hidden',
                                                                boxSizing: 'border-box',
                                                                transition: 'all 0.3s ease-in-out',
                                                                opacity: transitioning ? 0.5 : 1,
                                                            }}
                                                            onClick={() => handleEventClick(item)}
                                                            onTransitionEnd={() => setTransitioning(false)}
                                                        >
                                                            <Stack direction="column" spacing={1.5}>
                                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', minHeight: 28 }}>
                                                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ flex: 1, minWidth: 0, gap: 0.5 }}>
                                                                        {userReg?.refunded && <Chip key="refunded" label="Refunded" size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: 'info.main', color: 'white' }} />}
                                                                        {userReg?.canceled && <Chip key="canceled" label="Canceled" size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: 'error.light', color: 'white' }} />}
                                                                        {userReg?.withdrawn && !userReg?.canceled && <Chip key="withdrawn" label="Withdrawn" size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: 'rgb(150, 100, 255)', color: 'white' }} />}
                                                                        {userReg?.disqualified && !userReg?.canceled && <Chip key="disqualified" label="Disqualified" size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: 'warning.light', color: 'white' }} />}
                                                                        {userReg && !userReg.refunded && !userReg.canceled && !userReg.withdrawn && !userReg.disqualified && (
                                                                            new Date(item.endDate).getTime() + 24 * 60 * 60 * 1000 > new Date().getTime()
                                                                                ? <Chip key="active" label="Active" size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: 'success.light', color: 'white' }} />
                                                                                : <Chip key="completed" label="Completed" size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: 'info.main', color: 'white' }} />
                                                                        )}
                                                                        {item.tags?.map((tag: string, idx: number) => (
                                                                            <Chip key={`tag-${idx}`} label={tag} size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: appTheme.palette.primary.light, color: 'white' }} />
                                                                        ))}
                                                                    </Stack>
                                                                    {process.env.REACT_APP_LEADERBOARD_URL && (
                                                                        <IconButton size="small" onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            if (isMobileView) {
                                                                                setLeaderboardDrawerEvent(item);
                                                                                setEditEvent(null);
                                                                                setMyEventsPopperEventId(null);
                                                                                setMyEventsPopperAnchorEl(null);
                                                                            } else {
                                                                                setStandingsEvent(item);
                                                                                setEditEvent(null);
                                                                            }
                                                                        }} sx={{ color: appTheme.palette.primary.main, flexShrink: 0 }} title="Standings"><LeaderboardIcon fontSize="small" /></IconButton>
                                                                    )}
                                                                    <IconButton size="small" onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (isMobileView) {
                                                                            setMyEventsPopperEventId(myEventsPopperEventId === item._id ? null : item._id);
                                                                            setEditEvent(null);
                                                                            setLeaderboardDrawerEvent(null);
                                                                        } else {
                                                                            setMyEventsPopperAnchorEl(myEventsPopperEventId === item._id ? null : e.currentTarget);
                                                                            setMyEventsPopperEventId(myEventsPopperEventId === item._id ? null : item._id);
                                                                        }
                                                                    }} sx={{ color: appTheme.palette.primary.main, flexShrink: 0 }}><InfoOutlinedIcon fontSize="small" /></IconButton>
                                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEventClick(item); }} sx={{ color: appTheme.palette.primary.main, flexShrink: 0 }} title="Registration details"><HowToRegIcon fontSize="small" /></IconButton>
                                                                </Stack>
                                                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, overflow: 'hidden' }}>
                                                                    <Stack direction="column" sx={{ flexShrink: 0, alignItems: 'center' }}>
                                                                        <Box
                                                                            sx={{
                                                                                maxWidth: imageWidth,
                                                                                maxHeight: isMobile ? 80 * factor : 100 * factor,
                                                                                overflow: 'hidden',
                                                                                borderRadius: 2.5,
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                            }}
                                                                        >
                                                                            <img
                                                                                src={item.eventCardImageURL ? (imageCache[item.eventCardImageURL] || item.eventCardImageURL) : QUILTED_FLAG_LOGO}
                                                                                alt={item.eventName}
                                                                                style={{
                                                                                    maxWidth: '100%',
                                                                                    maxHeight: isMobile ? 80 * factor : 100 * factor,
                                                                                    width: 'auto',
                                                                                    height: 'auto',
                                                                                    display: 'block',
                                                                                    verticalAlign: 'middle',
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    </Stack>
                                                                    <Stack direction="column" spacing={1} sx={{ flex: 1, minWidth: 0, overflow: 'hidden', alignItems: isMobile ? 'center' : 'stretch' }}>
                                                                        <Stack direction="column" spacing={0.5} sx={{ alignItems: isMobile ? 'center' : 'flex-start', width: '100%' }}>
                                                                            <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600, textAlign: isMobile ? 'center' : 'left', overflow: 'hidden', wordBreak: 'break-word', minWidth: 0, fontSize: isMobile ? '0.95rem' : undefined }}>{item.eventName}</Typography>
                                                                            <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} sx={{ fontWeight: 500, textAlign: isMobile ? 'center' : 'left', overflow: 'hidden', wordBreak: 'break-word', minWidth: 0, fontSize: isMobile ? '0.8rem' : undefined }}>@ {item.courseName}</Typography>
                                                                            {isMobile ? (
                                                                                <Stack direction="column" spacing={0.25} alignItems="center">
                                                                                    <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: '0.75rem', textAlign: 'center' }}>{formatEventDate(item)}</Typography>
                                                                                    {userReg && !userReg.refunded && !userReg.canceled && !userReg.withdrawn && !userReg.disqualified && <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: '0.75rem', textAlign: 'center' }}>{formatEventTimeOrTBD(item, userReg)}</Typography>}
                                                                                </Stack>
                                                                            ) : (
                                                                                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ gap: 0.5 }}>
                                                                                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>{formatEventDate(item)}</Typography>
                                                                                    {userReg && !userReg.refunded && !userReg.canceled && !userReg.withdrawn && !userReg.disqualified && <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>{formatEventTimeOrTBD(item, userReg)}</Typography>}
                                                                                </Stack>
                                                                            )}
                                                                        </Stack>
                                                                    </Stack>
                                                                </Stack>
                                                            </Stack>
                                                        </Paper>
                                                    </Box>
                                                );
                                            })}
                                    </Stack>
                                    </Stack>

                                    <Popover
                                        open={!isMobileView && !!myEventsPopperAnchorEl && !!myEventsPopperEventId}
                                        anchorEl={myEventsPopperAnchorEl}
                                        onClose={() => { setMyEventsPopperAnchorEl(null); setMyEventsPopperEventId(null); }}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                        slotProps={{ paper: { elevation: 4 } }}
                                        sx={{ zIndex: 1300 }}
                                        disableScrollLock
                                    >
                                        {myEventsPopperEventId && (() => {
                                            const event = userRegisteredEvents.events.find((e: any) => e._id === myEventsPopperEventId);
                                            return event ? <MyEventsInfoPopperContent event={event} /> : null;
                                        })()}
                                    </Popover>

                                    {isMobileView && (!!editEvent || !!leaderboardDrawerEvent || !!myEventsPopperEventId) && (
                                        <Drawer
                                            anchor="bottom"
                                            variant="temporary"
                                            open={true}
                                            onClose={handleCloseMobileDrawer}
                                            transitionDuration={2500}
                                            sx={{
                                                '& .MuiDrawer-paper': {
                                                    height: (editEvent || leaderboardDrawerEvent) ? '90vh' : '60vh',
                                                    width: '95vw',
                                                    left: '50%',
                                                    marginLeft: '-47.5vw',
                                                    maxHeight: (editEvent || leaderboardDrawerEvent) ? '90vh' : '60vh',
                                                    borderTopLeftRadius: 8,
                                                    borderTopRightRadius: 8,
                                                    padding: 2,
                                                    paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
                                                    boxSizing: 'border-box',
                                                    transition: 'height 2500ms ease-in-out',
                                                },
                                            }}
                                        >
                                            <Stack direction="column" sx={{ height: '100%', width: '100%', overflow: 'hidden', minHeight: 0 }}>
                                                <Stack direction="row" justifyContent="flex-end" padding={0.5} sx={{ flexShrink: 0 }}>
                                                    <IconButton size="small" onClick={handleCloseMobileDrawer} sx={{ color: appTheme.palette.primary.main }} title="Close"><CloseIcon fontSize="small" /></IconButton>
                                                </Stack>
                                                <Box sx={{ flex: 1, minHeight: 0, overflow: editEvent ? 'auto' : 'hidden' }}>
                                                    {editEvent ? (
                                                        <ShowRegistration />
                                                    ) : leaderboardDrawerEvent ? (
                                                        <LeaderboardView event={leaderboardDrawerEvent} headless hideSelector />
                                                    ) : myEventsPopperEventId ? (
                                                        (() => {
                                                            const event = userRegisteredEvents.events.find((e: any) => e._id === myEventsPopperEventId);
                                                            return event ? <MyEventsInfoPopperContent event={event} /> : null;
                                                        })()
                                                    ) : null}
                                                </Box>
                                                {editEvent && (
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" paddingTop={2} flexShrink={0} sx={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
                                                        <Button variant="outlined" color="primary" onClick={handleViewLeaderboard}>View Leaderboard</Button>
                                                        <Button variant="contained" color="primary" onClick={handleCloseMobileDrawer}>Close</Button>
                                                    </Stack>
                                                )}
                                            </Stack>
                                        </Drawer>
                                    )}

                                    {!isMobileView && (
                                        <Stack direction={'column'} spacing={0} flex={2} minWidth={280}
                                            sx={{
                                                padding: `${5 * factor}px ${20 * factor}px`,
                                                paddingBottom: `max(${6 * factor}px, env(safe-area-inset-bottom))`,
                                                boxShadow: 4,
                                                transition: 'all 0.3s ease-in-out',
                                                opacity: transitioning ? 0.5 : 1,
                                                overflow: 'hidden',
                                                maxHeight: `calc(100dvh - ${100 * factor}px - env(safe-area-inset-bottom))`,
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}
                                        >
                                            <Stack display={standingsEvent ? 'flex' : 'none'} direction="row" justifyContent="flex-end" padding={0.5} sx={{ flexShrink: 0 }}>
                                                <IconButton size="small" onClick={() => setStandingsEvent(null)} sx={{ color: appTheme.palette.primary.main }} title="Close"><CloseIcon fontSize="small" /></IconButton>
                                            </Stack>
                                            <Box sx={{ flex: 1, minHeight: 0, overflow: standingsEvent ? 'hidden' : 'auto', WebkitOverflowScrolling: 'touch', '&::-webkit-scrollbar': { display: 'none' } }}>
                                                {standingsEvent ? (
                                                    <LeaderboardView event={standingsEvent} headless hideSelector />
                                                ) : (
                                                    <ShowRegistration />
                                                )}
                                            </Box>
                                            <Stack display={editEvent ? 'flex' : 'none'} direction={'row'} spacing={1} sx={{ flexShrink: 0, justifyContent: 'flex-end', alignItems: 'center', paddingTop: '5px' }}>
                                                <Button variant="outlined" color="primary" onClick={handleViewLeaderboard}>View Leaderboard</Button>
                                                <Button variant="contained" color="primary" onClick={handleClose}>Close</Button>
                                            </Stack>
                                        </Stack>
                                    )}
                                </>
                            ) : (
                                <Stack direction={'column'} spacing={2} flex={3} ref={stackRef}
                                    sx={{
                                        padding: 3,
                                        overflow: 'hidden',
                                        alignItems: 'center',
                                        maxHeight: `calc(100dvh - ${120 * factor}px - env(safe-area-inset-bottom))`,
                                        minHeight: 0,
                                    }}
                                >
                                    <Stack direction="row" spacing={2} sx={{ width: '100%', alignItems: 'center', flexShrink: 0 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: appTheme.palette.primary.main }}>My Trips</Typography>
                                    </Stack>

                                    <Stack
                                        direction={!isMobileView ? 'column' : 'row'}
                                        flexWrap={!isMobileView ? 'nowrap' : 'wrap'}
                                        sx={{
                                            flex: 1,
                                            minHeight: 0,
                                            overflowY: 'auto',
                                            overflowX: 'hidden',
                                            gap: 2 * factor,
                                            justifyContent: !isMobileView ? 'flex-start' : 'center',
                                            alignItems: !isMobileView ? 'stretch' : 'flex-start',
                                            alignContent: 'flex-start',
                                            width: '100%',
                                            boxSizing: 'border-box',
                                            WebkitOverflowScrolling: 'touch',
                                            '&::-webkit-scrollbar': { display: 'none' },
                                        }}
                                    >
                                        {userTrips.length === 0 && (
                                            <Typography variant="subtitle1" sx={{ fontWeight: 500, color: appTheme.palette.primary.main }}>
                                                No trips found...
                                            </Typography>
                                        )}
                                        {userTrips.map((trip: any) => (
                                            <Box key={trip._id} sx={{
                                                flex: !isMobileView ? '0 0 auto' : (isMobileLayout ? '1 1 100%' : '1 1 0%'),
                                                minWidth: isMobileLayout ? 280 : 280,
                                                maxWidth: 550,
                                                width: !isMobileView ? '100%' : undefined,
                                                boxSizing: 'border-box',
                                            }}>
                                                <Paper
                                                    elevation={2}
                                                    sx={{
                                                        width: '100%',
                                                        padding: 1.5 * factor,
                                                        border: `1px solid ${appTheme.palette.secondary.main}`,
                                                        position: 'relative',
                                                        overflow: 'hidden',
                                                        boxSizing: 'border-box',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.3s ease-in-out',
                                                        '&:hover': { backgroundColor: 'action.hover' },
                                                    }}
                                                    onClick={() => setSelectedTrip(trip)}
                                                >
                                                    <Stack direction="column" spacing={1.5} sx={{ width: '100%', minWidth: 0 }}>
                                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>{trip.groupName}</Typography>
                                                        <Stack direction="row" spacing={1.5} alignItems="stretch" sx={{ minWidth: 0 }}>
                                                            <Box
                                                                sx={{
                                                                    flex: 1,
                                                                    minWidth: 0,
                                                                    minHeight: 80 * factor,
                                                                    overflow: 'hidden',
                                                                    borderRadius: 2,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <img
                                                                    src={trip.groupCardImageURL ? (imageCache[trip.groupCardImageURL] || trip.groupCardImageURL) : QUILTED_FLAG_LOGO}
                                                                    alt={trip.groupName}
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        objectFit: 'cover',
                                                                        objectPosition: 'center',
                                                                    }}
                                                                />
                                                            </Box>
                                                            <Stack direction="column" spacing={1} sx={{ flex: 2, minWidth: 0, overflow: 'hidden', justifyContent: 'center' }}>
                                                                {isTripInFuture(trip) ? (
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{getTripCountdown(trip)}</Typography>
                                                                ) : (
                                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{formatTripDateRange(trip)}</Typography>
                                                                )}
                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{trip.eventIDs?.length ?? 0} {trip.eventIDs?.length === 1 ? 'event' : 'events'}</Typography>
                                                                {trip.ownerName && (
                                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Owner: {trip.ownerName}</Typography>
                                                                )}
                                                            </Stack>
                                                        </Stack>
                                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                            {[trip.city, trip.state].filter(Boolean).join(', ') || '—'}
                                                            {isTripInFuture(trip) && (
                                                                <> · {formatTripDateRange(trip)}</>
                                                            )}
                                                        </Typography>
                                                    </Stack>
                                                </Paper>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Stack>
                            )}
                        </>
                    )}

                    {/* My Teams tab */}
                    {tabValue === 'teams' && (
                        <Box sx={{ flex: 1, padding: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 500, color: appTheme.palette.primary.main }}>
                                Team Management Functions Coming Soon!
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </PullToRefresh>
        </Stack>
    );
};
